"use client";

import Link from "next/link";
import { History, Rocket, Zap } from "lucide-react";
import { RouteHero, RouteHeroMark } from "@/features/home-v2/RouteHero";
import TileActions from "@/features/home-v2/TileActions";
import { StatePanel } from "@/features/home-v2/ui";
import { useScrollReveal } from "@/features/home-v2/useScrollReveal";
import { useBrowseParams } from "@/lib/browse/params";
import { dropFigures } from "../dropFigures";
import { canViewDrop } from "../permissions";
import type { Drop } from "../types";
import DropSpotlight from "./DropSpotlight";
import DropTile from "./DropTile";
import "./drops.css";
import { useGridColumns } from "@/hooks/useGridColumns";
const DROP_TABS = ["live-upcoming", "past"] as const;
type DropTab = (typeof DROP_TABS)[number];

/**
 * /drops, in v2. Every rule is the legacy page's:
 *
 *  - PUBLIC LISTING: only `accept` / `active` drops (canViewDrop) — the list
 *    endpoint has been seen to return the caller's own drafts with a token.
 *  - the phase is the derived one (status AND times), so an accepted drop
 *    starting next week is upcoming, not advertised as live.
 *  - the tab lives in `?tab=`, so the past view can be linked and Back
 *    returns to it.
 *  - like and save on every tile, through the app's own toggles.
 *
 * New: twelve at a time, more as the reader scrolls.
 */
export default function DropsBrowse({
  initialDrops,
  lang
}: {
  initialDrops: Drop[];
  lang: "fa" | "en";
}) {
  const isFa = lang === "fa";
  const t = (fa: string, en: string) => isFa ? fa : en;
  const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
  const {
    tab,
    set
  } = useBrowseParams<DropTab>({
    tabs: DROP_TABS,
    defaultTab: "live-upcoming"
  });
  const active: DropTab = tab ?? "live-upcoming";
  const visibleDrops = initialDrops.filter(d => canViewDrop(d.rawStatus, false));
  const live = visibleDrops.filter(d_0 => d_0.status === "minting");
  const upcoming = visibleDrops.filter(d_1 => d_1.status === "upcoming");
  const liveAndUpcoming = [...live, ...upcoming];
  // "Minting now" counts only drops with something left to mint.
  const mintingNow = live.filter(d_2 => {
    const fig = dropFigures(d_2);
    return !fig.soldOut && !fig.empty;
  }).length;
  const past = visibleDrops.filter(d_3 => d_3.status === "ended");
  const list = active === "past" ? past : liveAndUpcoming;
  const grid = useGridColumns();
  const reveal = useScrollReveal({
    columns: grid.columns,
    loaded: list.length,
    resetKey: active
  });
  const tabs = [{
    key: "live-upcoming" as const,
    label: t("لایو و به‌زودی", "Live & upcoming"),
    Icon: Zap,
    n: liveAndUpcoming.length
  }, {
    key: "past" as const,
    label: t("گذشته", "Past"),
    Icon: History,
    n: past.length
  }];
  return <div className="v2-page dr-page">
      <RouteHero eyebrow={<><Rocket aria-hidden />{t("دراپ‌ها", "Drops")}</>} title={t("دراپ‌ها", "Drops")} description={t("جدیدترین دراپ‌های NFT را کشف کن و پیش از دیگران مینت کن.", "Discover the latest NFT drops on NFTBaz and mint before they're gone.")} aside={<RouteHeroMark icon={<Zap />} value={nf.format(mintingNow)} caption={t("دراپ در حال مینت", "minting now")} />} />

      {active === "live-upcoming" && liveAndUpcoming.length > 0 ? <DropSpotlight drops={liveAndUpcoming} lang={lang} /> : null}

      <nav className="dr-tabs" aria-label={t("دراپ‌ها", "Drops")}>
        {tabs.map(({
        key,
        label,
        Icon,
        n
      }) => <button key={key} type="button" className="dr-tab v2-focus" aria-pressed={active === key} onClick={() => set({
        tab: key
      })}>
            <Icon aria-hidden />{label}<b data-zero={n === 0 || undefined}>{nf.format(n)}</b>
          </button>)}
      </nav>

      {list.length === 0 ? <StatePanel title={active === "past" ? t("هنوز دراپی تمام نشده", "No past drops yet") : t("الان دراپ فعالی نیست", "No live or upcoming drops right now")} body={active === "past" ? t("دراپ‌هایی که مینتشان تمام شود این‌جا می‌مانند.", "Drops land here once their mint closes.") : t("دراپ‌های بعدی به‌محض اعلام این‌جا می‌آیند.", "The next drops appear here as soon as they are announced.")} action={<Link className="v2-btn v2-btn--ghost v2-btn--sm v2-focus" href={`/${lang}/market`}>{t("مشاهده بازار", "Browse the market")}</Link>} /> : <>
          <ul ref={grid.ref} className="dr-grid">
            {list.slice(0, reveal.count).map(d_4 => <li key={d_4.id}>
                <TileActions model="drop" id={d_4.likeId} lang={lang} initialSaved={d_4.current_user?.watchlist ?? false} like={{
            initialLiked: d_4.current_user?.like ?? false,
            count: d_4.likes?.count ?? 0
          }}>
                  <DropTile drop={d_4} lang={lang} />
                </TileActions>
              </li>)}
          </ul>
          {reveal.hasMore ? <div className="dr-more" ref={reveal.sentinel}>
              <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={reveal.more}>{t("دراپ‌های بیشتر", "More drops")}</button>
            </div> : null}
        </>}
    </div>;
}